import { parseApiDateMs } from '../../utils/parseApiDate';
import { DESK_COLUMNS } from './deskConfig';

/** MS-4.10 — global desk search toolbar options */
export const DESK_SEARCH_SIZES = [5, 10, 15, 25, 50, 100];
export const DEFAULT_DESK_SEARCH_SIZE = 25;
export const DESK_SEARCH_MODES = ['hybrid', 'keyword', 'semantic', 'graph'];
export const DEFAULT_DESK_SEARCH_MODE = 'hybrid';
export const DESK_WINDOW_DAYS = [1, 7, 30];
export const DEFAULT_DESK_SEARCH_DAYS = 1;

const DESK_SEARCH_MODE_LABELS = {
  hybrid: 'Hybrid',
  keyword: 'Keyword',
  semantic: 'Semantic',
  graph: 'Graph',
};

export function parseDeskSearchSize(raw) {
  const n = parseInt(raw, 10);
  return DESK_SEARCH_SIZES.includes(n) ? n : DEFAULT_DESK_SEARCH_SIZE;
}

export function parseDeskSearchMode(raw) {
  const mode = (raw || '').toLowerCase();
  return DESK_SEARCH_MODES.includes(mode) ? mode : DEFAULT_DESK_SEARCH_MODE;
}

export function parseDeskSearchDays(raw) {
  const n = parseInt(raw, 10);
  return DESK_WINDOW_DAYS.includes(n) ? n : DEFAULT_DESK_SEARCH_DAYS;
}

export function deskSearchModeLabel(mode) {
  return DESK_SEARCH_MODE_LABELS[mode] || DESK_SEARCH_MODE_LABELS.hybrid;
}

export function buildDeskSearchUrlParams({ q, size, mode, days }) {
  const params = new URLSearchParams();
  const trimmed = (q || '').trim();
  if (trimmed) {
    params.set('q', trimmed);
  }
  if (size !== DEFAULT_DESK_SEARCH_SIZE) {
    params.set('size', String(size));
  }
  if (mode !== DEFAULT_DESK_SEARCH_MODE) {
    params.set('mode', mode);
  }
  if (days !== DEFAULT_DESK_SEARCH_DAYS) {
    params.set('days', String(days));
  }
  return params;
}

export function buildWebGraphUrl(query, { layout = 'starburst' } = {}) {
  const trimmed = (query || '').trim();
  if (!trimmed) {
    return null;
  }
  const params = new URLSearchParams();
  params.set('q', trimmed);
  params.set('depth', '2');
  if (layout === 'constellation') {
    params.set('layout', 'constellation');
  }
  return `/web?${params.toString()}`;
}

export function resolveActiveDeskQuery(committedPageQ, committedColQ) {
  const pageQ = (committedPageQ || '').trim();
  if (pageQ) {
    return pageQ;
  }
  for (const col of DESK_COLUMNS) {
    const colQ = (committedColQ[col.id] || '').trim();
    if (colQ) {
      return colQ;
    }
  }
  return '';
}

/** Mirrors API {@code DeskCategoryMapper} + desk feed category wiring. */
export const FEED_CATEGORY_TO_DESK = {
  business: 'markets',
  finance: 'markets',
  economics: 'markets',
  politics: 'geopolitics',
  news: 'geopolitics',
  world: 'geopolitics',
  technology: 'techscience',
  science: 'techscience',
  health: 'techscience',
};

const DESK_COLUMN_IDS = new Set(DESK_COLUMNS.map((col) => col.id));

export function mapArticleToDeskColumn(article) {
  const labels = [];
  if (Array.isArray(article?.categories)) {
    article.categories.forEach((c) => {
      if (c) labels.push(String(c).trim().toLowerCase());
    });
  }
  const legacy = (article?.category || '').trim().toLowerCase();
  if (legacy) labels.push(legacy);

  for (const category of labels) {
    if (category && FEED_CATEGORY_TO_DESK[category]) {
      return FEED_CATEGORY_TO_DESK[category];
    }
  }

  const tags = (article?.tags || []).map((t) => String(t).toLowerCase());
  for (const tag of tags) {
    if (FEED_CATEGORY_TO_DESK[tag]) {
      return FEED_CATEGORY_TO_DESK[tag];
    }
    if (tag.includes('market') || tag.includes('finance')) return 'markets';
    if (tag.includes('politic') || tag.includes('war') || tag.includes('geo')) return 'geopolitics';
    if (tag.includes('tech') || tag.includes('science') || tag.includes('health')) return 'techscience';
  }

  return null;
}

export function partitionArticlesByDesk(articles) {
  const columns = Object.fromEntries(DESK_COLUMNS.map((col) => [col.id, []]));

  (articles || []).forEach((article) => {
    const deskId = mapArticleToDeskColumn(article);
    if (deskId && DESK_COLUMN_IDS.has(deskId)) {
      columns[deskId].push(article);
    }
  });

  return columns;
}

/** Short acronyms (FCC, SEC) perform poorly with semantic-only retrieval. */
export function isAcronymQuery(query) {
  const trimmed = (query || '').trim();
  if (!trimmed) return false;
  if (trimmed.length <= 4) return true;
  return trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed);
}

export function articleMatchesQueryTokens(query, article) {
  const tokens = (query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return false;
  const haystack = `${article?.title || ''} ${article?.summary || ''}`.toLowerCase();
  return tokens.some((token) => haystack.includes(token));
}

/** Merge keyword + semantic; prefer higher articleRank when present (SDM-34). */
export function mergeHybridArticles(keywordArticles, semanticArticles) {
  const byId = new Map();
  const prefer = (existing, incoming) => {
    const er = existing?.articleRank;
    const ir = incoming?.articleRank;
    if (typeof er === 'number' && typeof ir === 'number') {
      return ir > er ? incoming : existing;
    }
    if (typeof ir === 'number' && typeof er !== 'number') {
      return incoming;
    }
    return existing;
  };
  (keywordArticles || []).forEach((article) => {
    if (!article?.id) return;
    byId.set(article.id, byId.has(article.id) ? prefer(byId.get(article.id), article) : article);
  });
  (semanticArticles || []).forEach((article) => {
    if (!article?.id) return;
    byId.set(article.id, byId.has(article.id) ? prefer(byId.get(article.id), article) : article);
  });
  const merged = Array.from(byId.values());
  const hasRanks = merged.some((a) => typeof a?.articleRank === 'number');
  if (hasRanks) {
    merged.sort((a, b) => {
      const ra = typeof a.articleRank === 'number' ? a.articleRank : -1;
      const rb = typeof b.articleRank === 'number' ? b.articleRank : -1;
      if (rb !== ra) return rb - ra;
      const ta = parseApiDateMs(a.publishedAt);
      const tb = parseApiDateMs(b.publishedAt);
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    });
  }
  return merged;
}

async function fetchFeedSearch(apiBase, {
  search,
  category,
  retrieval,
  semantic,
  size,
  days = DEFAULT_DESK_SEARCH_DAYS,
  seedTerms,
}) {
  const params = new URLSearchParams({
    page: '0',
    size: String(size),
    sortBy: 'relevance',
    sortDir: 'desc',
    days: String(days),
  });
  if (search) {
    params.set('search', search);
  }
  if (category) {
    params.set('category', category);
  }
  if (retrieval) {
    params.set('retrieval', retrieval);
  } else if (semantic) {
    params.set('semantic', 'true');
  }
  (seedTerms || []).forEach((term) => {
    const trimmed = (term || '').trim();
    if (trimmed) {
      params.append('terms[]', trimmed);
    }
  });
  const response = await fetch(`${apiBase}/feed?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Feed search error ${response.status}`);
  }
  const data = await response.json();
  return data.content || [];
}

/** HYB-30 — single server hybrid call (RRF K+V+G). */
export async function fetchColumnHybridSearch(
  apiBase,
  category,
  query,
  { size = DEFAULT_DESK_SEARCH_SIZE, days = DEFAULT_DESK_SEARCH_DAYS, seedTerms } = {},
) {
  const trimmed = query.trim();
  if (!trimmed) {
    return { articles: [], mode: 'none' };
  }

  const articles = await fetchFeedSearch(apiBase, {
    search: trimmed,
    category,
    retrieval: 'hybrid',
    size,
    days,
    seedTerms,
  });
  return { articles, mode: articles.length ? 'hybrid' : 'none' };
}

/** MS-4.10 / HYB-30 — route column fetch by global search mode. */
export async function fetchColumnSearch(
  apiBase,
  category,
  query,
  {
    size = DEFAULT_DESK_SEARCH_SIZE,
    mode = DEFAULT_DESK_SEARCH_MODE,
    days = DEFAULT_DESK_SEARCH_DAYS,
    seedTerms,
  } = {},
) {
  const trimmed = query.trim();
  if (!trimmed) {
    return { articles: [], mode: 'none' };
  }

  if (mode === 'hybrid') {
    return fetchColumnHybridSearch(apiBase, category, trimmed, { size, days, seedTerms });
  }

  const retrieval = mode === 'graph' || mode === 'keyword' || mode === 'semantic' ? mode : 'hybrid';
  const articles = await fetchFeedSearch(apiBase, {
    search: trimmed,
    category,
    retrieval,
    size,
    days,
  });
  return { articles, mode: articles.length ? retrieval : 'none' };
}

/** Sprint 52 — idle default hybrid via server terms[] multi-query RRF. */
export async function fetchColumnDefaultHybrid(
  apiBase,
  category,
  terms,
  { size = DEFAULT_DESK_SEARCH_SIZE, days = DEFAULT_DESK_SEARCH_DAYS } = {},
) {
  const cleaned = (terms || []).map((term) => (term || '').trim()).filter(Boolean);
  if (!cleaned.length) {
    return [];
  }

  const params = new URLSearchParams({
    page: '0',
    size: String(size),
    sortBy: 'relevance',
    sortDir: 'desc',
    category,
    days: String(days),
    retrieval: 'hybrid',
  });
  cleaned.forEach((term) => {
    params.append('terms[]', term);
  });

  const response = await fetch(`${apiBase}/feed?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Feed default hybrid error ${response.status}`);
  }
  const data = await response.json();
  return data.content || [];
}

export async function fetchDeskSeeds(apiBase) {
  const response = await fetch(`${apiBase}/v1/desk/seeds`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Desk seeds error ${response.status}`);
  }
  const data = await response.json();
  const byColumn = Object.fromEntries(DESK_COLUMNS.map((col) => [col.id, []]));
  (data.columns || []).forEach((entry) => {
    if (entry?.columnId && Array.isArray(entry.terms)) {
      byColumn[entry.columnId] = entry.terms;
    }
  });
  return byColumn;
}

export async function fetchColumnRankedBrowse(
  apiBase,
  category,
  { size = DEFAULT_DESK_SEARCH_SIZE, days = DEFAULT_DESK_SEARCH_DAYS } = {},
) {
  const params = new URLSearchParams({
    page: '0',
    size: String(size),
    sortBy: 'relevance',
    sortDir: 'desc',
    category,
    days: String(days),
  });
  const response = await fetch(`${apiBase}/feed?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`Feed ranked browse error ${response.status}`);
  }
  const data = await response.json();
  return data.content || [];
}

/** @deprecated use fetchColumnRankedBrowse — desk idle uses feedWeight articleRank */
export async function fetchColumnRecency(
  apiBase,
  category,
  options = {},
) {
  return fetchColumnRankedBrowse(apiBase, category, options);
}

export function emptyDeskColumnQueries() {
  return Object.fromEntries(DESK_COLUMNS.map((col) => [col.id, '']));
}

export function mirrorDeskColumnQueries(term) {
  const value = term || '';
  return Object.fromEntries(DESK_COLUMNS.map((col) => [col.id, value]));
}

/** Prefer graph > hybrid > keyword > semantic when aggregating column modes. */
function resolveDeskResultMode(modes) {
  if (!modes || modes.length === 0) return 'none';
  if (modes.includes('graph')) return 'graph';
  if (modes.includes('hybrid')) return 'hybrid';
  if (modes.includes('keyword')) return 'keyword';
  if (modes.includes('semantic')) return 'semantic';
  return 'none';
}

/** Independent per-column queries — empty columns load feedWeight-ranked browse. */
export async function fetchDeskMixedSearch(
  apiBase,
  columnQueries,
  {
    sizePerColumn = DEFAULT_DESK_SEARCH_SIZE,
    mode: searchMode = DEFAULT_DESK_SEARCH_MODE,
    days = DEFAULT_DESK_SEARCH_DAYS,
    columns: columnDefs = DESK_COLUMNS,
    seedsByColumn = {},
    autoSearchMap = {},
  } = {},
) {
  const columnResults = await Promise.all(
    columnDefs.map(async (col) => {
      const trimmed = (columnQueries[col.id] || '').trim();
      if (!trimmed) {
        const articles = await fetchColumnRankedBrowse(apiBase, col.feedCategory, {
          size: sizePerColumn,
          days,
        });
        return [col.id, { articles, mode: 'none' }];
      }
      const seedTerms =
        autoSearchMap[col.id] === true ? (seedsByColumn[col.id] || []) : [];
      const result = await fetchColumnSearch(apiBase, col.feedCategory, trimmed, {
        size: sizePerColumn,
        mode: searchMode,
        days,
        seedTerms,
      });
      return [col.id, result];
    }),
  );

  const columns = Object.fromEntries(columnResults.map(([id, result]) => [id, result.articles]));
  const modes = columnResults.map(([, result]) => result.mode);
  const searchedCount = columnDefs.filter((col) => (columnQueries[col.id] || '').trim()).length;
  const total = columnResults.reduce((sum, [, result]) => sum + result.articles.length, 0);
  const mode = resolveDeskResultMode(modes);

  return { columns, total, mode, searchedCount };
}

/** Update only columns whose committed query changed; leave other columns untouched. */
export async function fetchDeskIncrementalColumnSearch(
  apiBase,
  committedColQ,
  previousCommittedColQ,
  previousColumns,
  {
    sizePerColumn = DEFAULT_DESK_SEARCH_SIZE,
    mode: searchMode = DEFAULT_DESK_SEARCH_MODE,
    days = DEFAULT_DESK_SEARCH_DAYS,
    columns: columnDefs = DESK_COLUMNS,
    seedsByColumn = {},
    autoSearchMap = {},
    onColumnArticles,
  } = {},
) {
  const columns = { ...(previousColumns || {}) };
  let total = 0;
  const modes = [];

  const columnResults = await Promise.all(
    columnDefs.map(async (col) => {
      const q = (committedColQ[col.id] || '').trim();
      const prevQ = (previousCommittedColQ[col.id] || '').trim();
      if (q === prevQ) {
        const articles = columns[col.id] || [];
        if (typeof onColumnArticles === 'function') {
          onColumnArticles(col.id, articles);
        }
        return { articles, mode: 'unchanged' };
      }
      if (q) {
        const seedTerms =
          autoSearchMap[col.id] === true ? (seedsByColumn[col.id] || []) : [];
        const result = await fetchColumnSearch(apiBase, col.feedCategory, q, {
          size: sizePerColumn,
          mode: searchMode,
          days,
          seedTerms,
        });
        columns[col.id] = result.articles;
        if (typeof onColumnArticles === 'function') {
          onColumnArticles(col.id, result.articles);
        }
        return { articles: result.articles, mode: result.mode };
      }
      const articles = await fetchColumnRankedBrowse(apiBase, col.feedCategory, {
        size: sizePerColumn,
        days,
      });
      columns[col.id] = articles;
      if (typeof onColumnArticles === 'function') {
        onColumnArticles(col.id, articles);
      }
      return { articles, mode: 'none' };
    }),
  );

  columnResults.forEach((result) => {
    total += result.articles.length;
    if (result.mode !== 'unchanged') {
      modes.push(result.mode);
    }
  });

  return { columns, total, mode: resolveDeskResultMode(modes) };
}

/** MS-4.7 / HYB-30 — per-column category-scoped retrieval (hybrid|keyword|semantic|graph). */
export async function fetchDeskHybridSearch(
  apiBase,
  query,
  {
    sizePerColumn = DEFAULT_DESK_SEARCH_SIZE,
    mode: searchMode = DEFAULT_DESK_SEARCH_MODE,
    days = DEFAULT_DESK_SEARCH_DAYS,
    columns: columnDefs = DESK_COLUMNS,
    seedsByColumn = {},
    autoSearchMap = {},
    /** Optional progressive paint: (columnId, articles) as each column resolves */
    onColumnArticles,
  } = {},
) {
  const trimmed = query.trim();
  if (!trimmed) {
    return { columns: partitionArticlesByDesk([]), total: 0, mode: 'none' };
  }

  const columnResults = await Promise.all(
    columnDefs.map(async (col) => {
      const seedTerms =
        autoSearchMap[col.id] === true ? (seedsByColumn[col.id] || []) : [];
      const result = await fetchColumnSearch(apiBase, col.feedCategory, trimmed, {
        size: sizePerColumn,
        mode: searchMode,
        days,
        seedTerms,
      });
      if (typeof onColumnArticles === 'function') {
        onColumnArticles(col.id, result.articles);
      }
      return [col.id, result];
    }),
  );

  const columns = Object.fromEntries(columnResults.map(([id, result]) => [id, result.articles]));
  const modes = columnResults.map(([, result]) => result.mode);
  const total = columnResults.reduce((sum, [, result]) => sum + result.articles.length, 0);

  return { columns, total, mode: resolveDeskResultMode(modes) };
}

/** @deprecated alias — MS-4.7 hybrid path */
export const fetchDeskSemanticSearch = fetchDeskHybridSearch;
